import { useEffect, useState } from 'react';
import { Table, SimpleGrid, Text, Stack, Paper, Group, Button } from '@mantine/core';
import { IconAdd as IconPlus, IconSpool } from '../../components/icons/CncIcons';
import EntityHeader from '../../components/EntityHeader';
import { ViewMode } from '../../components/TableGridToggle';
import { api, imageUrl } from '../../api/client';
import ConsumableModal from './ConsumableModal';

export interface Consumable {
  id: number;
  name: string;
  notes: string | null;
  imagePath: string | null;
  typicalCostEur: number;
  markupPct: number;
  typicalPriceEur: number | null;
}

export default function ConsumablesPage() {
  const [view, setView] = useState<ViewMode>('table');
  const [rows, setRows] = useState<Consumable[]>([]);
  const [editingId, setEditingId] = useState<number | 'new' | null>(null);

  async function refresh() {
    const list = await api.get<Consumable[]>('/consumables');
    setRows(list);
  }
  useEffect(() => { refresh(); }, []);

  return (
    <Stack>
      <EntityHeader title="Lab Consumables" icon={<IconSpool size={26} />} view={view} onView={setView} />

      {view === 'table' ? (
        <Paper withBorder>
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ width: 80 }}>Image</Table.Th>
                <Table.Th>Name</Table.Th>
                <Table.Th style={{ width: 140 }}>Cost (EUR)</Table.Th>
                <Table.Th style={{ width: 100 }}>Markup</Table.Th>
                <Table.Th style={{ width: 140 }}>Price (EUR)</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((r) => (
                <Table.Tr key={r.id} className="click-row" onClick={() => setEditingId(r.id)}>
                  <Table.Td>
                    <div className="image-box">
                      {r.imagePath && <img src={imageUrl(r.imagePath, true)} alt={r.name} />}
                    </div>
                  </Table.Td>
                  <Table.Td>{r.name}</Table.Td>
                  <Table.Td>{r.typicalCostEur != null ? r.typicalCostEur.toFixed(2) : '—'}</Table.Td>
                  <Table.Td>{r.markupPct}%</Table.Td>
                  <Table.Td>{r.typicalPriceEur != null ? r.typicalPriceEur.toFixed(2) : '—'}</Table.Td>
                </Table.Tr>
              ))}
              <Table.Tr>
                <Table.Td colSpan={5}>
                  <Button fullWidth variant="light" leftSection={<IconPlus size={14} />} onClick={() => setEditingId('new')} data-tour="consumables-add">
                    Add Consumable
                  </Button>
                </Table.Td>
              </Table.Tr>
            </Table.Tbody>
          </Table>
        </Paper>
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2, md: 3, lg: 4 }}>
          {rows.map((r) => (
            <div key={r.id} className="grid-card" onClick={() => setEditingId(r.id)}>
              <div className="card-image">
                {r.imagePath ? <img src={imageUrl(r.imagePath)} alt={r.name} /> : <Text c="dimmed" size="xs">No image</Text>}
              </div>
              <Group justify="space-between">
                <Text fw={600}>{r.name}</Text>
                <Text size="sm" c="orange">{r.typicalPriceEur != null ? `€${r.typicalPriceEur.toFixed(2)}` : '—'}</Text>
              </Group>
            </div>
          ))}
          <div className="add-card" onClick={() => setEditingId('new')}>
            <Group gap={6}><IconPlus size={16} /><Text>Add Consumable</Text></Group>
          </div>
        </SimpleGrid>
      )}

      {editingId !== null && (
        <ConsumableModal
          id={editingId === 'new' ? null : editingId}
          onClose={() => setEditingId(null)}
          onSaved={async () => { await refresh(); setEditingId(null); }}
          onDeleted={async () => { await refresh(); setEditingId(null); }}
        />
      )}
    </Stack>
  );
}
